'use client';

import { Box } from '@mui/material';
import { Link } from '@/primitives';
import Ilr2CaptionText from '@/components/publicSearch/animal/ebv/Ilr2CaptionText';
import { ROUTE_SEGMENT } from '@/constants/routeSegments';
import type { EbvAverageData } from '@/hooks/publicSearch/ebv/ebvColumns';
import {
  resolveDetailAvgCaptions,
  shouldStackBlackRedBreedAverages,
  type BreedAverageBand,
  type BreedAverageCaptionLine,
} from '@/components/publicSearch/animal/ebv/breedAverageLabels';

type BreedAverageGridRowLabelProps = {
  ebvAverage: EbvAverageData;
  isBlackRedAngus: boolean;
  showPercentileSearch: boolean;
  orgCode: string;
  societyId?: string | number;
  onAverageRowClick?: () => void;
};

function percentileHref(orgCode: string, band?: BreedAverageBand) {
  const base = `/${orgCode}/${ROUTE_SEGMENT.ANIMALS}/ebv-percentages`;
  return band ? `${base}?band=${band}` : base;
}

function percentileLinkLabel(band: BreedAverageBand) {
  return band === 'B' ? 'View Black Percentiles' : 'View Red Percentiles';
}

const compactLineSx = {
  display: 'block',
  lineHeight: 1.15,
  fontSize: '12px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
} as const;

function CaptionWithOptionalLink({
  line,
  orgCode,
  showPercentileSearch,
}: {
  line: BreedAverageCaptionLine;
  orgCode: string;
  showPercentileSearch: boolean;
}) {
  return (
    <Box component="span" sx={compactLineSx}>
      <Ilr2CaptionText raw={line.caption} />
      {showPercentileSearch && line.band ? (
        <>
          {' '}
          <Link
            href={percentileHref(orgCode, line.band)}
            onClick={(event) => event.stopPropagation()}
            sx={{
              display: 'inline',
              fontWeight: 500,
              textDecoration: 'none',
              whiteSpace: 'nowrap',
              fontSize: 'inherit',
            }}
          >
            {percentileLinkLabel(line.band)}
          </Link>
        </>
      ) : null}
    </Box>
  );
}

export default function BreedAverageGridRowLabel({
  ebvAverage,
  isBlackRedAngus,
  showPercentileSearch,
  orgCode,
  onAverageRowClick,
}: BreedAverageGridRowLabelProps) {
  const dualCaptions = shouldStackBlackRedBreedAverages(
    isBlackRedAngus,
    ebvAverage,
  );
  const lines = resolveDetailAvgCaptions(
    dualCaptions,
    ebvAverage,
    showPercentileSearch,
  );

  if (lines.length === 0) return null;

  if (!dualCaptions) {
    const line = lines[0];
    if (!line) return null;
    const caption = <Ilr2CaptionText raw={line.caption} />;

    if (showPercentileSearch && onAverageRowClick) {
      return (
        <span
          onClick={(event) => {
            event.stopPropagation();
            onAverageRowClick();
          }}
          style={{
            cursor: 'pointer',
            textDecoration: 'underline',
            color: 'inherit',
            fontSize: '12px',
            lineHeight: 1.15,
          }}
        >
          {caption}
        </span>
      );
    }

    if (showPercentileSearch) {
      return (
        <Box
          component="span"
          sx={{
            display: 'inline',
            fontSize: '12px',
            lineHeight: 1.15,
          }}
        >
          {caption}{' '}
          <Link
            href={percentileHref(orgCode)}
            onClick={(event) => event.stopPropagation()}
            sx={{
              display: 'inline',
              fontWeight: 500,
              textDecoration: 'none',
              whiteSpace: 'nowrap',
              fontSize: 'inherit',
            }}
          >
            View Percentiles
          </Link>
        </Box>
      );
    }

    return (
      <Box component="span" sx={{ fontSize: '12px', lineHeight: 1.15 }}>
        {caption}
      </Box>
    );
  }

  return (
    <Box sx={{ fontSize: '12px', lineHeight: 1.15 }}>
      {lines.map((line) => (
        <CaptionWithOptionalLink
          key={line.band ?? line.caption}
          line={line}
          orgCode={orgCode}
          showPercentileSearch={showPercentileSearch}
        />
      ))}
    </Box>
  );
}
